import React from 'react';
import { Box, Typography } from '@mui/material';
import { ClinicHeader, InfoGrid } from '../../../../../utils/reportUtils';
import { usePatientContext } from '../../../../../components/patinetProvider';
import { toTitleCase } from '../../../../../utils/commonUtils';
import { EmbryoTransferType } from '../../../../../components/Surgery/embryoTransfer/embryoTransfer.helpers';

interface Props {
  data: EmbryoTransferType;
}

const EmbryoTransferReport: React.FC<Props> = ({ data }) => {
  const { MainpatientDetails } = usePatientContext();

  const patientInfo: string[][] = [
    [
      'Patient Name',
      `${toTitleCase(MainpatientDetails?.prefix)} ${toTitleCase(MainpatientDetails?.first_name)} ${toTitleCase(MainpatientDetails?.last_name)}`.trim() || 'N/A',
      'Patient ID',
      MainpatientDetails?.medical_record_no ?? 'N/A',
    ],
    [
      'Age',
      MainpatientDetails?.age ?? 'N/A',
      'Gender',
      toTitleCase(MainpatientDetails?.gender) || 'N/A',
    ],
    [
      'Partner Name',
      `${toTitleCase(MainpatientDetails?.partner?.first_name)} ${toTitleCase(MainpatientDetails?.partner?.last_name)}`.trim() || 'N/A',
      'Contact',
      MainpatientDetails?.mobile_number ?? 'N/A',
    ],
  ];

  const clinicalDetails: string[][] = [
    ['Clinician', data.clinician_name ?? 'N/A', 'Embryologist', data.embryologist_name ?? 'N/A'],
    ['Witness Embryologist', data.witness_embryologist_name ?? 'N/A', 'Nurse', data.nurse_name ?? 'N/A'],
    ['ET Type', toTitleCase(data.transfer_method) ?? 'N/A', 'Anesthesia', toTitleCase(data.anesthesia) ?? 'N/A'],
    ['Anesthetist', data.anesthetist_name ?? 'N/A', 'Performed Under Anesthesia', data.performed_under_anesthesia ? 'Yes' : 'No'],
    ['Start Time', data.start_time ?? 'N/A', 'End Time', data.end_time ?? 'N/A'],
    ['Transfer Date', data.date ?? 'N/A', 'Cycle Start Date', data.cycle_start_date ?? 'N/A'],
  ];

  const procedureDetails: string[][] = [
    ['Ultrasound Used', data.is_ultrasound ? 'Yes' : 'No', 'Ultrasound Method', toTitleCase(data.ultrasound_method) ?? 'N/A'],
    ['Endometrium Thickness', data.endometrium_thickness != null ? data.endometrium_thickness.toString() : 'N/A', 'Endometrium Pattern', toTitleCase(data.endometrium_pattern) ?? 'N/A'],
    [
      'Transfer Depth (cm)', 
      data.transfer_depth != null ? data.transfer_depth.toString() : 'N/A', 
      'Distance from Fundus (cm)', 
      data.distance_from_fundus != null ? data.distance_from_fundus.toString() : 'N/A'
    ],      
    ['Catheter Used', toTitleCase(data.catheter) ?? 'N/A', 'Catheter Tube', data.catheter_tube ? 'Yes' : 'No'],
    ['Catheter Remarks', data.catheter_remarks ?? 'N/A', 'No of Attempts', data.no_of_attempts?.toString() ?? 'N/A'],
    ['Embryo Retained', data.embryo_retained?.toString() ?? 'N/A', 'Difficulty Level', toTitleCase(data.difficulty_level) ?? 'N/A'],
    ['Embryo Glue Used', data.is_embryo_glue_used ? 'Yes' : 'No', 'Tenaculum Used', data.is_tenaculum_used ? 'Yes' : 'No'],
    ['Stylet Used', data.is_stylet_used ? 'Yes' : 'No', '', ''],
  ];

  const pregnancyDetails: string[][] = [
    ['Pregnancy Date', data.pregnancy_date ?? 'N/A', 'Cycle Closed', data.is_cycle_closed ? 'Yes' : 'No'],
    ['Cycle Close Reason', data.cycle_close_reason ?? 'N/A', '', ''],
  ];

  const currentDateTime = new Date().toLocaleString();

  return (
    <Box sx={{ p: 5, backgroundColor: '#fff', color: '#333', fontFamily: 'Arial, sans-serif', position: 'relative', minHeight: '100%' }}>
      <ClinicHeader />

      <Box component="hr" sx={{ my: 1, borderColor: '#fff' }} />

      {/* Patient Info */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          Patient Details
        </Typography>
        <InfoGrid data={patientInfo} />
      </Box>

      {/* Clinical Info */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          Clinical Info
        </Typography>
        <InfoGrid data={clinicalDetails} />
      </Box>

      {/* Procedure Info */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          Procedure Details
        </Typography>
        <InfoGrid data={procedureDetails} />
      </Box>

      {/* Pregnancy Info */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          Pregnancy Details
        </Typography>
        <InfoGrid data={pregnancyDetails} />
      </Box>

      {/* Additional Notes */}
      {data.notes && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            Additional Notes
          </Typography>
          <Typography variant="body2">{data.notes}</Typography>
        </Box>
      )}

      {/* Complications */}
      {data.complications && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            Complications
          </Typography>
          <Typography variant="body2">{data.complications}</Typography>
        </Box>
      )}

      {/* Footer Timestamp */}
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          color: '#666',
          fontStyle: 'italic',
        }}
      >
        Report generated on: {currentDateTime}
      </Typography>
    </Box>
  );
};

export default EmbryoTransferReport;
